import { UserProfile, UserMetadata, ChatPreferences, DeveloperPreferences, ClientConfig, ConsentConfig } from '../types/user';
import { AuthUser } from '../types/auth';

export class User implements UserProfile {
  public readonly id: string;
  public displayName: string;
  public email: string;
  public avatarUrl?: string;
  public status: 'active' | 'inactive' | 'pending';
  public language: string;
  public timezone: string;
  public roles: string[];
  public chatPreferences: ChatPreferences;
  public metadata: UserMetadata;
  public clientConfig?: ClientConfig;
  public consent?: ConsentConfig;
  public tenant: string | null;
  public allowedTenants: string[];
  public createdAt: string;
  public updatedAt: string;

  constructor(data: Partial<UserProfile> = {}) {
    this.id = data.id || '';
    this.displayName = data.displayName || '';
    this.email = data.email || '';
    this.avatarUrl = data.avatarUrl;
    this.status = data.status || 'active';
    this.language = data.language || 'en';
    this.timezone = data.timezone || 'Europe/Amsterdam'; // CET default
    this.roles = data.roles || ['user'];
    this.chatPreferences = data.chatPreferences || {};
    this.metadata = {
      developerPreferences: { cacheDebugEnabled: false },
      onboardingCompleted: false,
      privacyConsent: false,
      privacyNoticeReadConsent: false,
      dataProcessingConsent: false,
      privacyNoticeVersion: undefined,
      lastOnboardingUpdated: undefined,
      useNickname: false,
      ...data.metadata
    };
    this.clientConfig = data.clientConfig;
    this.consent = data.consent;
    this.tenant = data.tenant || null;
    this.allowedTenants = data.allowedTenants || [];
    this.createdAt = data.createdAt || new Date().toISOString();
    this.updatedAt = data.updatedAt || new Date().toISOString();
  }

  // Type-safe getters and setters
  get developerPreferences(): DeveloperPreferences {
    return this.metadata?.developerPreferences || { 
      cacheDebugEnabled: false 
    };
  }

  set developerPreferences(value: DeveloperPreferences) {
    this.metadata = {
      ...this.metadata,
      developerPreferences: value
    };
    this.updatedAt = new Date().toISOString();
  }

  get isActive(): boolean {
    return this.status === 'active';
  }

  get isAdmin(): boolean {
    return this.roles.includes('admin');
  }

  /**
   * Check if user has a specific role
   */
  hasRole(role: string): boolean {
    return this.roles.includes(role);
  }

  /**
   * Check if user has completed onboarding
   * Requires all three consent flags to be true
   */
  hasCompletedOnboarding(): boolean {
    return (
      this.metadata?.onboardingCompleted === true &&
      this.metadata?.privacyNoticeReadConsent === true &&
      this.metadata?.dataProcessingConsent === true
    );
  }

  /**
   * Check if user has given privacy consent
   */
  hasPrivacyConsent(): boolean {
    return this.metadata?.privacyConsent || false;
  }

  /**
   * Update user metadata
   */
  updateMetadata(updates: Partial<UserMetadata>): void {
    this.metadata = { ...this.metadata, ...updates };
    this.updatedAt = new Date().toISOString();
  }

  /**
   * Update chat preferences
   */
  updateChatPreferences(updates: Partial<ChatPreferences>): void {
    this.chatPreferences = { ...this.chatPreferences, ...updates };
    this.updatedAt = new Date().toISOString();
  }

  // Mood management is now handled exclusively through UserFacts
  // Use userFactsService for mood operations

  toJSON(): UserProfile {
    return {
      id: this.id,
      displayName: this.displayName,
      email: this.email,
      avatarUrl: this.avatarUrl,
      status: this.status,
      language: this.language,
      timezone: this.timezone,
      roles: this.roles,
      chatPreferences: this.chatPreferences,
      metadata: {
        ...this.metadata,
        // Remove sensitive fields for serialization
        tenantId: undefined,
        homeAccountId: undefined
      },
      clientConfig: this.clientConfig,
      consent: this.consent,
      createdAt: this.createdAt,
      updatedAt: this.updatedAt
    };
  }

  static fromAuthContext(authUser: AuthUser | any, backendProfile?: Partial<UserProfile>): User {
    // Handle MSAL AccountInfo format
    // For MSAL: Extract 'oid' from idTokenClaims to match x-ms-client-principal-id header
    // The 'oid' claim is what apiService.getUserId() sends in the header for MSAL
    // For OIDC: Use localAccountId which is set to profile.sub
    const idTokenClaims = authUser.idTokenClaims as { oid?: string; sub?: string } | undefined;
    
    // Priority: oid (Azure AD Object ID, matches x-ms-client-principal-id header) > localAccountId > id
    // Note: 'oid' is unique per user per tenant, used by backend for user identification
    const userId = idTokenClaims?.oid || authUser.localAccountId || authUser.id || '';
    
    const normalizedAuthUser: AuthUser = {
      id: userId,
      email: authUser.username || authUser.email || '',
      displayName: authUser.name || authUser.displayName || '',
      tenantId: authUser.tenantId,
      homeAccountId: authUser.homeAccountId
    };

    // IMPORTANT: For existing users, prioritize backend profile data over auth data
    // Only use auth data as fallback for new users or when backend fields are empty
    // The user.id must come from auth (oid claim for MSAL) for SignalR routing
    return new User({
      ...backendProfile,
      id: normalizedAuthUser.id,  // Must use auth ID for SignalR routing (matches x-ms-client-principal-id header)
      // Prioritize backend data - only fall back to auth data if backend is empty
      displayName: backendProfile?.displayName || normalizedAuthUser.displayName || '',
      email: backendProfile?.email || normalizedAuthUser.email || '',
      metadata: {
        tenantId: normalizedAuthUser.tenantId,
        homeAccountId: normalizedAuthUser.homeAccountId,
        ...(backendProfile?.metadata || {}),
        developerPreferences: backendProfile?.metadata?.developerPreferences || { cacheDebugEnabled: false }
      }
    });
  }

  updateProfile(updates: Partial<UserProfile>): User {
    return new User({
      ...this.toJSON(),
      ...updates,
      updatedAt: new Date().toISOString()
    });
  }

  /**
   * Create User instance from API response
   */
  static fromApiResponse(data: any): User {
    return new User(data);
  }

  /**
   * Create a sanitized version for API requests (removes sensitive data)
   */
  toApiPayload(): Partial<UserProfile> {
    const { tenantId, homeAccountId, ...safeMetadata } = this.metadata;
    return {
      id: this.id,
      displayName: this.displayName,
      email: this.email,
      avatarUrl: this.avatarUrl,
      status: this.status,
      language: this.language,
      roles: this.roles,
      chatPreferences: this.chatPreferences,
      metadata: safeMetadata,
      clientConfig: this.clientConfig,
      consent: this.consent,
      createdAt: this.createdAt,
      updatedAt: this.updatedAt
    };
  }
}
